import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { pollMockupStatus, getMockupPartialImageUrl } from '../../util/api';
import { mockPollMockupStatus, MOCK_MODE } from '../../util/mockup-api-mock';
import {
  startMockupUpload,
  updateUploadStatus,
  updateUploadProgress,
  completeMockupUpload,
  failMockupUpload,
  selectMockupUpload,
} from '../../ducks/mockupUpload.duck';
import {
  getFirstPendingRequest,
  updateRequestStatus,
  clearRequest,
  getListingContext,
} from '../../util/mockup-manager';
import { createMockupStorage } from '../../services/MockupStorageService';
import { useTrackEvent } from '../../context/AnalyticsContext';

/**
 * MockupPollManager - Global polling manager for room mockup processing
 *
 * This component:
 * - Checks for pending requests on app mount and resumes polling
 * - Listens for new upload triggers via Redux state
 * - Updates global Redux state with polling progress
 * - Handles network errors gracefully by continuing to poll
 * - Manages completion/failure and localStorage cleanup
 */
const MockupPollManager = () => {
  const dispatch = useDispatch();
  const { pollingTriggeredAt, currentJobId, listingContext } = useSelector(selectMockupUpload);
  const currentUser = useSelector(state => state.user.currentUser);
  const activePollingRef = useRef(null);
  const isAuthenticated = !!currentUser?.id;
  const { trackEvent } = useTrackEvent();

  // Polling function with network error resilience
  const startPolling = (jobId) => {
    // Prevent duplicate polling for the same job
    if (activePollingRef.current === jobId) {
      console.log('[MockupPollManager] Already polling job:', jobId);
      return;
    }

    console.log('[MockupPollManager] Starting polling for job:', jobId);
    activePollingRef.current = jobId;

    // Use mock API if MOCK_MODE is enabled, otherwise use real API
    const pollFunction = MOCK_MODE ? mockPollMockupStatus : pollMockupStatus;

    pollFunction(jobId, (statusData) => {
      // Update progress status in both Redux and localStorage
      if (statusData.status) {
        updateRequestStatus(jobId, statusData.status);
        // Use updateUploadProgress to also pass partial image URL
        // Convert relative URL to full URL with API base
        dispatch(updateUploadProgress(statusData.status, {
          partialImageUrl: getMockupPartialImageUrl(statusData.partialImageUrl),
          partialIndex: statusData.partialIndex,
        }));
      }
    })
      .then((result) => {
        // Check if job was not found (silent cleanup)
        if (result.notFound) {
          console.log('[MockupPollManager] Job not found on server, clearing local state');
          clearRequest(jobId);
          activePollingRef.current = null;
          dispatch(failMockupUpload('Job not found'));
          return;
        }

        // Job completed successfully
        console.log('[MockupPollManager] Job completed:', jobId);

        const roomMockupData = {
          originalImageUrl: result.originalImageUrl,
          processedImageUrl: result.processedImageUrl,
          processed: result.processed,
          processingError: result.processingError,
          wallSegmentation: result.wallSegmentation || null,
          artworkPlacement: {
            x: 0.5,
            y: 0.4,
          },
          uploadedAt: result.uploadedAt,
          metadata: result.metadata,
        };

        // Save to appropriate storage (localStorage or API based on auth)
        const mockupStorage = createMockupStorage({
          isAuthenticated,
          dispatch,
          currentUser,
        });

        mockupStorage.save(roomMockupData, jobId)
          .then(() => {
            console.log('[MockupPollManager] Mockup data saved successfully');
          })
          .catch((err) => {
            console.error('[MockupPollManager] Failed to save mockup data:', err);
          });

        clearRequest(jobId);

        // Update global state to completed
        dispatch(completeMockupUpload());

        // Track mockup processing completed event (for triggering surveys)
        const jobListingContext = getListingContext(jobId) || listingContext;
        trackEvent('mockup_processing_completed', {
          listing_id: jobListingContext?.listingId,
          listing_title: jobListingContext?.listingTitle,
          is_authenticated: isAuthenticated,
        });

        activePollingRef.current = null;
      })
      .catch((err) => {
        // Check if this is a network error vs server error
        const isNetworkError = err.message.includes('fetch') ||
                               err.message.includes('network') ||
                               err.message.includes('NetworkError') ||
                               !err.status; // No HTTP status = network issue

        if (isNetworkError) {
          // Network error - log and continue polling (don't fail permanently)
          console.warn('[MockupPollManager] Network error during polling, will retry:', err.message);
          // The polling function will automatically retry after its interval
        } else {
          // Server error or processing failure - fail permanently
          console.error('[MockupPollManager] Polling failed permanently:', err);
          clearRequest(jobId);
          dispatch(failMockupUpload(err.message || 'Failed to process image'));
          activePollingRef.current = null;
        }
      });
  };

  // Check for pending requests on mount (resume polling after refresh)
  useEffect(() => {
    const pendingRequest = getFirstPendingRequest();
    if (pendingRequest) {
      // Skip resuming requests that have already failed
      if (pendingRequest.status === 'failed') {
        console.log('[MockupPollManager] Found failed request on mount, skipping resume:', pendingRequest.jobId);
        return;
      }

      console.log('[MockupPollManager] Found pending request on mount, resuming:', pendingRequest.jobId);

      // Restore Redux state from localStorage
      const listingContext = getListingContext(pendingRequest.jobId);
      if (listingContext) {
        // Dispatch to restore Redux state (this will show the banner)
        dispatch(startMockupUpload(pendingRequest.jobId, listingContext));
      }

      // Start polling
      startPolling(pendingRequest.jobId);
    }
  }, []); // Only run once on mount

  // Listen for new polling triggers
  useEffect(() => {
    if (pollingTriggeredAt && currentJobId) {
      console.log('[MockupPollManager] Polling triggered for job:', currentJobId);
      startPolling(currentJobId);
    }
  }, [pollingTriggeredAt]); // Trigger when timestamp changes

  // Cleanup on unmount (though this component should never unmount)
  useEffect(() => {
    return () => {
      if (activePollingRef.current) {
        console.log('[MockupPollManager] Component unmounting, polling will continue');
      }
    };
  }, []);

  // This component doesn't render anything
  return null;
};

export default MockupPollManager;
